import React from 'react';
import Icon from '../../../components/AppIcon';

const CertificationTimeline = () => {
  const timeline = [
    {
      year: "2015",
      quarter: "Q2",
      milestone: "Microsoft Gold Partnership",
      description: "Achieved Microsoft Gold Partner status with Azure Solutions and Microsoft 365 certifications, establishing enterprise cloud capabilities.",
      certifications: ["Azure Solutions Architect", "Microsoft 365 Enterprise Administrator"],
      icon: "Award",
      color: "from-primary to-secondary"
    },
    {
      year: "2016",
      quarter: "Q3",
      milestone: "Zoho Premium Partner",
      description: "Became Zoho Premium Partner with comprehensive CRM and business application implementation expertise across multiple industries.",
      certifications: ["Zoho CRM Consultant", "Zoho One Implementation"],
      icon: "Star",
      color: "from-secondary to-accent"
    },
    {
      year: "2017",
      quarter: "Q1",
      milestone: "Acronis Elite Partnership",
      description: "Secured Acronis Elite Partner designation specializing in cybersecurity, backup solutions, and disaster recovery implementations.",
      certifications: ["Acronis Cyber Protect Specialist", "Disaster Recovery Expert"],
      icon: "Shield",
      color: "from-accent to-primary"
    },
    {
      year: "2019",
      quarter: "Q4",
      milestone: "Advanced Security Certifications",
      description: "Expanded security portfolio with advanced certifications in threat intelligence, compliance management, and enterprise security architecture.",
      certifications: ["Azure Security Engineer", "Acronis Cyber Infrastructure"],
      icon: "Lock",
      color: "from-primary to-accent"
    },
    {
      year: "2021",
      quarter: "Q2",
      milestone: "AI & Automation Expertise",
      description: "Developed AI and automation capabilities with Power Platform, cognitive services, and intelligent workflow certifications.",
      certifications: ["Power Platform Developer", "AI Cognitive Services"],
      icon: "Zap",
      color: "from-secondary to-primary"
    },
    {
      year: "2023",
      quarter: "Q3",
      milestone: "Innovation Lab Launch",
      description: "Established Innovation Lab for emerging technology partnerships, beta integrations, and next-generation solution development.",
      certifications: ["Azure AI Engineer", "Advanced Analytics Specialist"],
      icon: "Rocket",
      color: "from-accent to-secondary"
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Calendar" size={18} color="var(--color-accent)" />
            <span className="text-accent text-sm font-medium">Partnership Evolution</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Certification Timeline & Milestones
          </h2>
          <p className="text-text-secondary text-sm md:text-base lg:text-lg max-w-3xl mx-auto">
            A decade of continuous growth and certification achievements building comprehensive technology partnership ecosystem for enterprise digital transformation.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-secondary to-accent"></div>

          <div className="space-y-8 md:space-y-12 lg:space-y-16">
            {timeline?.map((item, index) => (
              <div
                key={index}
                className={`relative flex flex-col lg:flex-row items-start lg:items-center gap-6 md:gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className="bg-card rounded-2xl shadow-lg p-6 md:p-8 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item?.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon name={item?.icon} size={24} color="#ffffff" />
                      </div>
                      <div className="text-left">
                        <div className="text-2xl md:text-3xl font-bold text-foreground">{item?.year}</div>
                        <div className="text-xs text-text-secondary">{item?.quarter}</div>
                      </div>
                    </div>

                    <h3 className="text-lg md:text-xl font-bold text-foreground mb-3 text-left">
                      {item?.milestone}
                    </h3>
                    <p className="text-sm md:text-base text-text-secondary mb-4 text-left">
                      {item?.description}
                    </p>

                    <div className="flex flex-wrap gap-2 justify-start">
                      {item?.certifications?.map((cert, certIndex) => (
                        <span
                          key={certIndex}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
                        >
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary items-center justify-center shadow-lg z-10">
                  <Icon name="CheckCircle2" size={28} color="#ffffff" />
                </div>

                <div className="w-full lg:w-5/12"></div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 md:mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="bg-card rounded-2xl p-6 md:p-8 shadow-md text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Award" size={32} color="var(--color-primary)" />
            </div>
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">25+</div>
            <div className="text-sm text-text-secondary">Active Certifications</div>
          </div>

          <div className="bg-card rounded-2xl p-6 md:p-8 shadow-md text-center">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="TrendingUp" size={32} color="var(--color-accent)" />
            </div>
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">8+</div>
            <div className="text-sm text-text-secondary">Years Partnership Growth</div>
          </div>

          <div className="bg-card rounded-2xl p-6 md:p-8 shadow-md text-center">
            <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={32} color="var(--color-secondary)" />
            </div>
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">15+</div>
            <div className="text-sm text-text-secondary">Technology Partners</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificationTimeline;